import { INHERIT_SENTINEL } from '../components/shared/counterHedgeMode';
import { createDraftStore } from './createDraftStore';
import type { DraftFieldCatalog } from './draftTypes';

/**
 * The opener entry row's editable controls.
 *
 * Fifteen of them. Only six ever had a committed counterpart (`saved*`); the
 * other nine compared against a baseline re-derived from the rule on every
 * render, which for a rule-less row is a CONSTANT — that asymmetry is why an
 * explicitly-switched-off toggle was indistinguishable from an untouched one.
 * Here they share ONE storage model, and differ in exactly one declared
 * property: whether "equal to the baseline" is an intention a control can
 * express (see `retireWhenEqualToBaseline`).
 *
 * ⚠️ Adding a control means adding it here. That is the point: the create
 * payload builder is derived from this catalog, so a column nobody wires up
 * fails its parity test instead of silently never reaching the POST.
 *
 * ⚠️ And everything here PERSISTS for 24 h unless it says otherwise. A toggle
 * staged today survives a reload and gets baked into whatever create fires
 * tomorrow — same as the text inputs, deliberately: splitting the family so
 * half the row survives a refresh and half does not is worse than either whole
 * answer.
 */
export const OPENER_DRAFT_FIELDS = {
  // All six are free-text inputs, so typing one back to the server's value is
  // the same as never having touched it — hence `retireWhenEqualToBaseline`.
  // The nine toggles land in the next PR and must NOT carry it.
  threshold: { kind: 'string', retireWhenEqualToBaseline: true },
  size: { kind: 'string', retireWhenEqualToBaseline: true },
  maxVol: { kind: 'string', retireWhenEqualToBaseline: true },
  walkSecs: { kind: 'string', retireWhenEqualToBaseline: true },
  walkCents: { kind: 'string', retireWhenEqualToBaseline: true },
  cap: { kind: 'string', retireWhenEqualToBaseline: true },

  // The nine selectors and toggles. NONE of them may declare
  // `retireWhenEqualToBaseline` — the type forbids it, and the reason is the
  // whole point of this catalog: on a rule-less row their baseline is a
  // constant, so an explicit OFF compares equal to untouched, and retiring the
  // cell would send the create with the key ABSENT and let the subgroup
  // snapshot fill it in.
  sizeMode: { kind: 'enum', values: ['dynamic', 'fixed'] },
  hedgeMode: {
    kind: 'enum',
    values: [
      INHERIT_SENTINEL,
      'always-threshold',
      'gtc-to-max-sum',
      'market-to-hedge'
    ]
  },
  lockstep: { kind: 'boolean' },
  polyGate: { kind: 'boolean' },
  kalshiGate: { kind: 'boolean' },
  polyPostOnly: { kind: 'boolean' },
  sweep: { kind: 'boolean' },
  forfait: { kind: 'boolean' },
  allowAboveOne: { kind: 'boolean' },

  // The two maker switches. NOT wired yet — the row still holds them in
  // `pendingFlags`; absorbing that is the next PR's work, and declaring them
  // here first is what lets the parity guard name them as a deliberate
  // exclusion rather than a gap.
  //
  // `persist: false` on purpose: an in-flight arm restored after a reload would
  // paint a leg ON that was never armed.
  polyMaker: { kind: 'boolean', persist: false },
  kalshiMaker: { kind: 'boolean', persist: false }
} as const satisfies DraftFieldCatalog;

export type OpenerDraftField = keyof typeof OPENER_DRAFT_FIELDS;

/**
 * The six text inputs hold raw strings; everything else holds its own type.
 * Derived from the catalog so a field added there without a value type here is
 * a compile error, not a silent `unknown`.
 */
export type OpenerDraftValues = {
  [K in OpenerDraftField]: (typeof OPENER_DRAFT_FIELDS)[K]['kind'] extends 'string'
    ? string
    : (typeof OPENER_DRAFT_FIELDS)[K] extends { values: readonly (infer V)[] }
      ? V
      : boolean;
};

export const openerDraftStore = createDraftStore({
  scope: 'opener',
  fields: OPENER_DRAFT_FIELDS
});

// A row only cleans its own key when it renders again and finds it expired —
// and the rows of a settled dated group never render again. Without a sweep,
// their keys stay for good: with a daily slate cadence that is hundreds of dead
// keys against a ~5 MB budget. Once at module load, and on every return to the
// tab.
if (typeof document !== 'undefined') {
  openerDraftStore.prune();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') openerDraftStore.prune();
  });
}
